import React from 'react';
import Container from 'react-bootstrap/Container';
import Stack from 'react-bootstrap/Stack';
import { ReactComponent as HutricsLogo } from 'svg/hutrics-logo-color.svg';
import { useAuth } from '../../../providers/auth-provider';
import { getCandidateValuesText } from '../shared/report-util';
import { getPercentileAttributes, getPercentileAttributesConstants } from '../../../api/dashboards-api';
import { REPORT_TYPE } from '../../../utils/constants';

function CandidateConfidentialPage({
  candidate,
  competencies = false,
  reportType = REPORT_TYPE.coaching,
  withDetailedInformation = true,
}) {
  const { isSelect } = useAuth();
  const percentilesAttributes = getPercentileAttributes();

  return (
    <Container className="bg-white mt-2 mb-3 candidate-profile-assessments">
      {candidate && (
        <Stack gap={0} className="m-auto w-75" style={{ paddingTop: '8em', paddingBottom: '4em' }}>
          <div className="text-center w-100" style={{ paddingBottom: '4em' }}>
            <HutricsLogo style={{ height: '8em' }} />
          </div>
          <div className="text-center pb-4" style={{ fontSize: '2em', fontWeight: 'bold', fontStyle: 'italic' }}>
            Confidential Document
          </div>
          <div className="text-center" style={{ fontSize: '1.5em' }}>
            Assessment Report for:
          </div>
          <div className="text-center" style={{ fontSize: '1.5em' }}>
            {candidate.name}
          </div>
          <div className="text-center pb-4 pt-4" style={{ fontSize: '1.25em' }}>
            Date of Assessment: {candidate.submitted}
          </div>
          <div className="pb-3">
            This report is a summary of the findings of an assessment conducted by Hutrics. It is designed to highlight
            the strengths and development needs of {candidate.name}. Because this information is of a personal nature,
            it is important that the following be considered:
          </div>
          <div>
            <ul>
              <li style={{ fontWeight: 'normal !important' }}>
                This information is confidential and should be stored in a secure file.
              </li>
              <li>
                This information is dated. Because people grow and develop over time, these results should be considered
                valid for no more than one year.
              </li>
              <li>This report should not be reproduced</li>
            </ul>
          </div>
          <div className="pb-2">
            To ensure that this information is used in the most helpful and ethical manner, this page should remain
            attached to this document at all times.
          </div>
          {withDetailedInformation && (
            <>
              <div className="pb-4">
                This document provides an integrated summary of information that was collected during the assessment
                process. The following assessments were used to gather that information
              </div>
              <div>
                <ul>
                  {candidate.percentiles[percentilesAttributes.personality] && <li>Hutrics Personality Assessment</li>}
                  {candidate.percentiles[percentilesAttributes.experience] && (
                    <li>Hutrics Assessment of Experiences</li>
                  )}
                  {candidate.percentiles[percentilesAttributes.values] && (
                    <li>Hutrics {` ${getCandidateValuesText(isSelect(), true)} `} Assessment</li>
                  )}
                  {candidate.percentiles.acumen && <li>Hutrics Acumen Assessment</li>}
                  {(candidate.percentiles.judgment ||
                    candidate.percentiles[getPercentileAttributesConstants().onsite.judgment]) && (
                    <li>Hutrics Situational Judgment Assessment</li>
                  )}
                  {competencies && (candidate.selfCompetencies?.scores || candidate.interviewCompetencies?.scores) && (
                    <li>Hutrics Competency Assessment</li>
                  )}
                </ul>
              </div>
            </>
          )}
          {withDetailedInformation && reportType !== REPORT_TYPE.staffing ? (
            <div>
              The following pages provide valuable feedback on the basis of the themes and trends identified in the
              assessment information.
            </div>
          ) : withDetailedInformation && reportType === REPORT_TYPE.staffing ? (
            <>
              <div className="pb-2">The following pages provide valuable feedback in three distinct ways:</div>
              <ol>
                <li>
                  The report profile will provide a recommendation and summary of the candidate&apos;s assessment based
                  on their predicted level of Loyalty and Performance for an ONSITE/HYBRID role
                </li>
                <li>
                  An additional report profile will provide a recommendation and summary of the candidate&apos;s
                  assessment based on their predicted level of Loyalty and Performance for a REMOTE role. The
                  recommendations and profiles may be different based on the role type (Onsite/Hybrid vs Remote)--this
                  is due to the variations inherent in these role types.
                </li>
                <li>
                  A Recruiting Profile section will provide an overview of key elements of the candidate profile
                  (org-specific, job-specific and manager-specific) to assist the recruiter in understanding what type
                  of environment (e.g., org-size, management philosophy) the candidate would be most successful.
                </li>
              </ol>
              <div className="pt-4">
                **NOTE: the predictive elements (e.g. personality) are different for ONSITE/HYBRID roles vs REMOTE
                roles. For example, it may be possible that the personality scores for a candidate could be RED for an
                ONSITE/HYBRID role and GREEN for a REMOTE role. This is due to the predictive elements for personality
                being different based on role type. This is similar for Values, Experience, and Judgment.**
              </div>
            </>
          ) : (
            <></>
          )}
        </Stack>
      )}
    </Container>
  );
}

export default CandidateConfidentialPage;
